import { Icon } from '@iconify/react'
import { useIsMutating } from '@tanstack/react-query'

import {
  Button,
  Dropdown,
  Menu,
  MenuItem,
  Tooltip,
  useGlobalState,
} from '@pepper/ui'

import { ReactComponent as FiDownloadCloudIcon } from 'assets/images/fi:download-cloud.svg'

import styles from './DownloadDropdown.module.css'

interface Props {
  handleCSVExport: () => void
  disableBulkDownload?: boolean
  handleDocsDownload?: () => void
}

type MenuItemsKeys = 'export-doc-links' | 'download-approved-docs'

function DownloadDropdown({
  handleCSVExport,
  handleDocsDownload,
  disableBulkDownload = false,
}: Props): JSX.Element {
  const { bulkDownloadJob } = useGlobalState()
  const isMutatingBulkDownloads = useIsMutating({
    mutationKey: ['bulk-export'],
  })

  const handleMenuClick = (key: MenuItemsKeys) => {
    switch (key) {
      case 'export-doc-links':
        handleCSVExport()
        break
      case 'download-approved-docs':
        handleDocsDownload?.()
        break
      default:
        // eslint-disable-next-line no-console
        console.error('unhandled case')
    }
  }

  const dropdownOverlay = () => {
    return (
      <Menu
        className={styles.menuContainer}
        onClick={event => handleMenuClick(event.key as MenuItemsKeys)}
      >
        <MenuItem key="export-doc-links" className={styles.menuItem}>
          <Icon icon="uil:file-export" width="16" height="16" />
          <span>Export Docs Links</span>
        </MenuItem>
        {handleDocsDownload && (
          <MenuItem
            key="download-approved-docs"
            className={styles.menuItem}
            disabled={
              bulkDownloadJob?.status === 'pending' ||
              isMutatingBulkDownloads > 0 ||
              disableBulkDownload
            }
          >
            {disableBulkDownload ? (
              <Tooltip
                className={styles.tooltip}
                title="This page does not contain any approved docs"
              >
                <Icon icon="uil:file-download" width="16" height="16" />
                <span>Download Approved Docs</span>
              </Tooltip>
            ) : (
              <>
                <Icon icon="uil:file-download" width="16" height="16" />
                <span>Download Approved Docs</span>
              </>
            )}
          </MenuItem>
        )}
      </Menu>
    )
  }

  return (
    <Dropdown trigger={['click']} overlay={dropdownOverlay}>
      <Button
        className={styles.dropdownButton}
        aria-label="Download Dropdown"
        icon={<FiDownloadCloudIcon width="16" height="16" />}
        data-id="business-assignment-tab-download-button"
      />
    </Dropdown>
  )
}

export default DownloadDropdown
